import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { NumericFormat } from 'react-number-format';
import { ILegalTransactionRelationshipRealEstate } from '../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { IRealEstatePurchaseContract } from '../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/IRealEstatePurchaseContract';
import { IRealEstateUseType } from '../../../Interfaces/IRealEstateUseType';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { PurchaseContractMembers } from '../PurchaseContractMembers';
import { ILegalClient } from '../../../Interfaces/ILegalClient';

interface IProps {
  formValues: ILegalTransactionRelationshipRealEstate;
  setFormValues: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationshipRealEstate>
  >;
  purchaseContract: IRealEstatePurchaseContract;
  setPurchaseContract: React.Dispatch<
    React.SetStateAction<IRealEstatePurchaseContract>
  >;
  realEstateUseTypes: IRealEstateUseType[];
  setIsWarningPrice: React.Dispatch<React.SetStateAction<boolean>>;
  isWarningPrice: boolean;
  setMembers: (newMembers: ILegalClient[]) => void;
  takenLegalClients: ILegalClient[];
  setTakenLegalClients: React.Dispatch<React.SetStateAction<ILegalClient[]>>;
}

export const RealEstatePriceUseType: React.FC<IProps> = ({
  formValues: { idRealEstateUseType, Defect, Particularities },
  setFormValues,
  purchaseContract,
  setPurchaseContract,
  realEstateUseTypes,
  setIsWarningPrice,
  isWarningPrice,
  setMembers,
  takenLegalClients,
  setTakenLegalClients,
}) => {
  const {
    Price,
    PriceInventoryOption,
    InventoryOptionArray,
    requiresClearance,
    clearanceDate,
    isRented,
    TenantArray,
  } = purchaseContract;

  return (
    <>
      <Typography variant='h5' sx={{ mt: 2, mb: 1 }}>
        Nutzung
        <br />
        <Typography>
          Falls Sie die Informationen über den Grundbucheintrag Ihres
          Vertragsgegenstandes haben, so tragen Sie diese bitte ein. Sofern Sie
          diese Informationen nicht vorliegen haben, ist dies kein Problem. Die
          Informationen können nachgereicht oder durch unsere Mitarbeiter im
          Grundbuch geprüft werden.
        </Typography>
      </Typography>
      <Box sx={{ mt: 5 }} />
      <Grid container spacing={2}>
        <Grid item sm={6}>
          <NumericFormat
            value={Price}
            suffix=' €'
            fixedDecimalScale
            thousandsGroupStyle='thousand'
            thousandSeparator='.'
            decimalSeparator=','
            fullWidth
            customInput={TextField}
            onValueChange={values => {
              if (values.floatValue !== undefined) {
                setIsWarningPrice(false);
                setPurchaseContract(x => ({
                  ...x,
                  Price: values.floatValue ?? 0,
                }));
              } else {
                setIsWarningPrice(true);
              }
            }}
            allowNegative={false}
            label='Kaufpreis'
            size='small'
            error={isWarningPrice}
          />
        </Grid>

        <Grid item sm={6}>
          <NumericFormat
            value={PriceInventoryOption}
            suffix=' €'
            fixedDecimalScale
            thousandsGroupStyle='thousand'
            thousandSeparator='.'
            decimalSeparator=','
            fullWidth
            customInput={TextField}
            onValueChange={values => {
              setPurchaseContract(x => ({
                ...x,
                PriceInventoryOption:
                  values.floatValue !== undefined ? values.floatValue : null,
              }));
            }}
            allowNegative={false}
            label='Anteil der Anlagen vom Kaufpreis'
            size='small'
            disabled={InventoryOptionArray.length === 0}
          />
        </Grid>

        <Grid item sm={12}>
          <FormControl fullWidth size='small' required>
            <InputLabel>Nutzungsüberlassung</InputLabel>
            <Select
              value={idRealEstateUseType}
              label='Nutzungsüberlassung'
              onChange={({ target: { value } }) =>
                setFormValues(x => ({
                  ...x,
                  idRealEstateUseType: Number(value),
                }))
              }
            >
              {realEstateUseTypes.map((x, i) => (
                <MenuItem key={i} value={x.idRealEstateUseType}>
                  {x.RealEstateUseType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item sm={12}>
          <TextField
            label='Mängel / Einschränkungen der Nutzbarkeit'
            size='small'
            fullWidth
            value={Defect ?? ''}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
              setFormValues(x => ({
                ...x,
                Defect: target.value || null,
              }))
            }
            multiline
            rows={3}
          />
        </Grid>

        <Grid item sm={12}>
          <TextField
            label='Besonderheiten'
            size='small'
            fullWidth
            value={Particularities ?? ''}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
              setFormValues(x => ({
                ...x,
                Particularities: target.value || null,
              }))
            }
            multiline
            rows={3}
          />
        </Grid>

        <Grid item sm={12} mt={6}>
          <FormControl component='fieldset'>
            <FormLabel component='legend'>Räumung notwendig?</FormLabel>
            <RadioGroup
              row
              value={requiresClearance ? 'yes' : 'no'}
              onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
                setPurchaseContract(x => ({
                  ...x,
                  requiresClearance: target.value === 'yes',
                  clearanceDate: target.value === 'yes' ? clearanceDate : null,
                }))
              }
            >
              <FormControlLabel value='yes' control={<Radio />} label='Ja' />
              <FormControlLabel value='no' control={<Radio />} label='Nein' />
            </RadioGroup>
          </FormControl>
        </Grid>

        {!!requiresClearance && (
          <Grid item sm={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
              <DatePicker
                label='Räumung bis (Datum)'
                openTo='year'
                views={['year', 'month', 'day']}
                value={clearanceDate ? dayjs(clearanceDate) : null}
                onChange={newValue =>
                  setPurchaseContract(x => ({
                    ...x,
                    clearanceDate: newValue
                      ? newValue.format('YYYY-MM-DD')
                      : '',
                  }))
                }
                format='DD.MM.YYYY'
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
        )}

        <Grid item sm={12} mt={6}>
          <FormControl component='fieldset'>
            <FormLabel component='legend'>Vermietet/Verpachtet?</FormLabel>
            <RadioGroup
              row
              value={isRented ? 'yes' : 'no'}
              onChange={e =>
                setPurchaseContract(x => ({
                  ...x,
                  isRented: e.target.value === 'yes',
                }))
              }
            >
              <FormControlLabel value='yes' control={<Radio />} label='Ja' />
              <FormControlLabel value='no' control={<Radio />} label='Nein' />
            </RadioGroup>
          </FormControl>
        </Grid>
        {isRented && (
          <Grid item sm={12}>
            <PurchaseContractMembers
              title='Mieter/Pächter'
              members={TenantArray}
              setMembers={(members: ILegalClient[]) =>
                setPurchaseContract(x => ({
                  ...x,
                  TenantArray: members,
                }))
              }
              takenLegalClients={takenLegalClients}
              setTakenLegalClients={setTakenLegalClients}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};
